import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';

/** Key `apiEnvelopeInterceptor` uses to smuggle the envelope's `meta` past the unwrap, attached directly on the returned array. */
export const PAGINATION_META_KEY = '__meta__';

export type PageMeta = {
  total: number;
  page: number;
  limit: number;
  lastPage: number;
}

export type PaginatedResult<T> = {
  items: T[];
  meta: PageMeta;
}

/** Backend's hard cap on `limit` (see API_RESPONSE_AND_PAGINATION_FRONTEND.md). */
export const MAX_PAGE_SIZE = 100;

/**
 * GET a list endpoint that follows the backend's paginated envelope
 * ({success, data, meta}, unwrapped by apiEnvelopeInterceptor into the array body
 * with `meta` attached as a hidden property). Centralizes that read so individual
 * services only build query params.
 */
export function getPaginated<T>(
  http: HttpClient,
  url: string,
  params?: HttpParams
): Observable<PaginatedResult<T>> {
  return http.get<T[]>(url, { params }).pipe(
    map((items) => ({
      items,
      meta: (items as unknown as Record<string, PageMeta>)[PAGINATION_META_KEY] ?? {
        total: items.length,
        page: 1,
        limit: items.length,
        lastPage: 1,
      },
    }))
  );
}

/** Clamps a requested page number to [1, meta.lastPage]. */
export function clampPage(meta: PageMeta | null, page: number): number {
  if (!meta) return Math.max(1, page);
  return Math.min(Math.max(1, page), meta.lastPage);
}
